import React, { useState } from "react";

import { useInterfaces } from "@/shared/api/interfaces";
import { Group } from "@/shared/components/settings-atoms";
import { AgentPage } from "@/shared/components/ui-kit";
import { INTERFACE_CATALOG } from "@/shared/lib/interface-catalog";
import { CatalogCard } from "./components/CatalogCard";
import { ConnectedInterfaces } from "./components/ConnectedInterfaces";
import { DisconnectDialog } from "./components/DisconnectDialog";
import { ClientAppSetup } from "./components/ClientAppSetup";
import { DiscordBotChannels } from "./components/DiscordBotChannels";
import { DiscordSetup } from "./components/DiscordSetup";
import { ExtensionSetup } from "./components/ExtensionSetup";
import { SlackSetup } from "./components/SlackSetup";
import { TeamsBotChannels } from "./components/TeamsBotChannels";
import { TeamsSetup } from "./components/TeamsSetup";
import { TelegramSetup } from "./components/TelegramSetup";
import { WhatsappSetup } from "./components/WhatsappSetup";
import { LINKED_TYPES } from "./shareLinks";

import type { Agent, Interface, InterfaceCategory, InterfaceType } from "@/shared/types";
import type { OpenDialog, SetupProps } from "./types";

/** The channels that can be connected today, and the dialog that connects each.
    The rest of the catalog shows as coming soon. */
const SETUP_FLOWS: Partial<Record<InterfaceType, React.ComponentType<SetupProps>>> = {
  telegram: TelegramSetup,
  slack: SlackSetup,
  whatsapp: WhatsappSetup,
  discord: DiscordSetup,
  teams: TeamsSetup,
  extension: ExtensionSetup,
  web: ClientAppSetup,
};

const isAlwaysOn = (type: InterfaceType) => (LINKED_TYPES as readonly string[]).includes(type);

const CATEGORIES: { name: InterfaceCategory; desc: string }[] = [
  { name: "Messaging", desc: "Meet people in the chat apps they already use." },
  { name: "Voice", desc: "The agent speaks — same brain, out loud." },
  { name: "Custom", desc: "Your product, your surface — the agent behind your own UI." },
];

/* Where people reach one agent: the interfaces it has (`/channels`, with
   their live state, errors and traffic), and the catalog to add another. */
export default function InterfacesPage({ agent, onOpenTraces, onSimulated, onViewChats }: {
  agent?: Agent | null;
  onOpenTraces?: () => void;
  /** A test message was accepted; the agent answers it on `threadId`. */
  onSimulated?: (iface: Interface, threadId: string) => void;
  /** "View chats" on a row: the conversations that came in through it. */
  onViewChats?: (iface: Interface) => void;
}) {
  const [open, setOpen] = useState<OpenDialog | null>(null);
  const list = useInterfaces(agent?.id ?? "");
  if (!agent) return null;

  const rows = list.data ?? [];
  const live = rows.filter((r) => r.status === "live" && r.connection === "ready").length;
  const messages = rows.reduce((n, r) => n + (r.stats?.messages_30d ?? 0), 0);
  const close = () => setOpen(null);
  const Setup = open?.dialog === "setup" ? SETUP_FLOWS[open.type] : undefined;
  const subtitle = rows.length
    ? `${live} live · ${messages} messages in the last 30 days`
    : list.isLoading
      ? "Loading…"
      : "Not reachable yet";

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <AgentPage
        agent={agent}
        title={`${agent.name} interfaces`}
        subtitle={subtitle}
        description={
          <>
            Where people reach {agent.name} — every interface talks to the same agent, with the same memory, apps
            and knowledge.
          </>
        }
      >
        {/* Header already ends in a divider — suppress the first group's own border */}
        <Group
          title="Connected"
          desc={`Every door into ${agent.name}. Pause a channel and it stops answering until you switch it back on.`}
          className="!border-t-0 !pt-0 !mt-0"
        >
          <ConnectedInterfaces
            agent={agent}
            list={list}
            onOpen={setOpen}
            onOpenTraces={onOpenTraces}
            onSimulated={onSimulated}
            onViewChats={onViewChats}
          />
        </Group>

        {CATEGORIES.map((category) => (
          <Group key={category.name} title={category.name} desc={category.desc}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {INTERFACE_CATALOG.filter((meta) => meta.category === category.name).map((meta) => (
                <CatalogCard
                  key={meta.type}
                  meta={meta}
                  connected={isAlwaysOn(meta.type) || rows.some((r) => r.type === meta.type)}
                  comingSoon={!SETUP_FLOWS[meta.type]}
                  onClick={() => setOpen({ dialog: "setup", type: meta.type })}
                />
              ))}
            </div>
          </Group>
        ))}
      </AgentPage>

      {Setup && open?.dialog === "setup" && <Setup agent={agent} existing={open.existing} onClose={close} />}
      {open?.dialog === "channels" && open.iface.type === "discord" && <DiscordBotChannels iface={open.iface} onClose={close} />}
      {open?.dialog === "channels" && open.iface.type === "teams" && <TeamsBotChannels iface={open.iface} onClose={close} />}
      {open?.dialog === "disconnect" && (
        <DisconnectDialog iface={open.iface} agentName={agent.name} onClose={close} />
      )}
    </div>
  );
}
